// src/pages/agent-api-sidebar.tsx
import { useEffect, useMemo, useCallback, useState } from "react";
import { Outlet, useNavigate } from "react-router-dom";
import { Button, Flex, Loader, View, Text } from "@aws-amplify/ui-react";
import { Auth } from "aws-amplify";

import { AgentApiConversationListed } from "../library/chat/conversation-listed";
import {
  useAgentApiAgentList,
  useAgentApiConversationList,
  Loadable,
} from "@heartcheck/shared-react";
import { Container } from "../library/container";
import { UploadNextRendered } from "../library/chat/upload-next-submitted";
import type { Agent, Conversation } from "@heartcheck/shared";

/**
 * Normalize a hybrid hook result (object, tuple, or plain Loadable)
 * into a consistent Loadable-like object.
 */
function normalizeLoadable<T = any>(raw: any): Loadable<T> {
  const stateCandidate: any =
    raw && raw.state ? raw.state : Array.isArray(raw) ? raw[0] : raw;

  if (stateCandidate && typeof stateCandidate.isUnloaded === "function") {
    return stateCandidate as Loadable<T>;
  }

  return Loadable.unloaded<T>();
}

/**
 * Extract an array of Conversation objects from a Loadable value.
 */
function extractConversationArray(convList: Loadable<any>): Conversation[] {
  const value = convList.value;
  if (!value) return [];

  if (typeof value.items === "function") {
    return (value.items() ?? []) as Conversation[];
  }

  if (Array.isArray(value)) {
    return value as Conversation[];
  }

  if (typeof value.values === "function") {
    return Array.from(value.values()) as Conversation[];
  }

  return [];
}

/**
 * Extract an array of Agent objects from a Loadable value.
 */
function extractAgentArray(agentList: Loadable<any>): Agent[] {
  const value = agentList.value;
  if (!value) return [];

  if (typeof value.items === "function") {
    return (value.items() ?? []) as Agent[];
  }

  if (Array.isArray(value)) {
    return value as Agent[];
  }

  if (typeof value.values === "function") {
    return Array.from(value.values()) as Agent[];
  }

  return [];
}

export function AIAgentSidebar() {
  const navigate = useNavigate();
  const [signingOut, setSigningOut] = useState(false);

  /**
   * Canonical sign-out landing page (static file on eu.heartcheckapp.com).
   * Ensure this exact URL is included in Cognito App Client logoutUrls.
   */
  const SIGNED_OUT_URL = "https://eu.heartcheckapp.com/signed-out";

  const handleSignOut = useCallback(async () => {
    if (signingOut) return;

    setSigningOut(true);
    try {
      // eslint-disable-next-line no-console
      console.log("[Sidebar] 🔐 Signing out via Auth.signOut()");
      await Auth.signOut();

      // Fallback redirect (Hosted UI may already redirect automatically)
      window.location.assign(SIGNED_OUT_URL);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("[Sidebar] ❌ Sign out failed:", err);
      window.location.assign(SIGNED_OUT_URL);
    } finally {
      setSigningOut(false);
    }
  }, [signingOut]);

  // Raw hybrid results
  const conversationsRaw = useAgentApiConversationList();
  const agentsRaw = useAgentApiAgentList();

  // Normalize to Loadable<T> (same pattern as NewChat/ViewChat)
  const conversationsList = normalizeLoadable(conversationsRaw);
  const agentsList = normalizeLoadable(agentsRaw);

  useEffect(() => {
    // eslint-disable-next-line no-console
    console.log("📥 Loaded AIAgentSidebar");
  }, []);

  const conversations = useMemo(
    () => extractConversationArray(conversationsList),
    [conversationsList]
  );

  const agents = useMemo(() => extractAgentArray(agentsList), [agentsList]);

  const loading =
    conversationsList.isUnloaded() ||
    conversationsList.loading === "loading" ||
    agentsList.isUnloaded() ||
    agentsList.loading === "loading";

  const sortedConversations = useMemo(
    () =>
      conversations
        .slice()
        .sort(
          (a, b) =>
            new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
        ),
    [conversations]
  );

  const conversationComponents = useMemo(
    () =>
      sortedConversations.map((conversation) => {
        const agent = agents.find((a) => a.id === conversation.agent);
        return (
          <AgentApiConversationListed
            key={conversation.id}
            conversation={conversation}
            agent={agent}
          />
        );
      }),
    [sortedConversations, agents]
  );

  return (
    <Flex
      direction="row"
      height="100dvh"
      style={{ minHeight: 0, overflow: "hidden" }}
    >
      <View
        className="sidebar"
        padding={16}
        style={{
          position: "relative",
          flex: "0 0 300px",
          width: "300px",
          height: "100%",
          minHeight: 0,
          overflowY: "auto",
        }}
      >
        <Container heading="Your Chats">
          <Flex
            direction="column"
            gap={10}
            style={{ maxHeight: "calc(100dvh - 220px)", overflowY: "auto" }}
          >
            {loading ? (
              <Flex height="120px" alignItems="center" justifyContent="center">
                <Loader />
              </Flex>
            ) : conversationComponents.length > 0 ? (
              conversationComponents
            ) : (
              <Text fontStyle="italic" color="font.secondary">
                No chats yet
              </Text>
            )}
          </Flex>

          <Button
            isFullWidth
            variation="primary"
            size="small"
            type="button"
            marginTop={16}
            onClick={() => navigate("/chat/new")}
            aria-label="Start a new chat"
          >
            New Chat
          </Button>
        </Container>

        <Container heading="Authentication">
          <Flex direction="column" gap={4} alignItems="flex-start">
            <Text fontWeight="bold">Signed in</Text>
            <Button
              isFullWidth
              variation="primary"
              size="small"
              marginTop={8}
              onClick={handleSignOut}
              aria-label="Sign out"
              isDisabled={signingOut}
            >
              {signingOut ? "Signing out…" : "Sign Out"}
            </Button>
          </Flex>
        </Container>

        <UploadNextRendered />
      </View>

      <View className="body" width="100%" overflow="auto">
        <Outlet />
      </View>
    </Flex>
  );
}
